import { useHistory, useQueryParams } from '@fs/zion-router'
import { useCallback, useRef } from 'react'
import { useSlsDispatchContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import {
  useSharedBrowserDispatchContext,
  useSharedBrowserStateContext,
} from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../../../providers/r2Provider/R2Context'

/**
 * useSetFocusedIndex - Use this hook when you need to change the focused index
 *
 * it sets:
 *
 * - the RibbonViewer's focused index
 * - the groupId in the url query params
 * - the image ark section in the url path
 * - the currentImageArk in the zionSlsState
 * @returns function that will set the focused index and update the image ark in the url and internal state
 */
export default function useSetFocusedIndex(doSwitchImageCheck = true) {
  const { setQuery, query } = useQueryParams({ replaceState: true })
  const isPageLoadRef = useRef(true)

  const history = useHistory()

  const overlayContext = useOverlayContext()
  const { switchImageCheck } = overlayContext

  const sharedBrowserState = useSharedBrowserStateContext()
  const sharedBrowserDispatch = useSharedBrowserDispatchContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const r2State = useR2StateContext()

  const { childGroups } = r2State

  const thumbnails = sharedBrowserState.thumbnails
  const { viewMode } = sharedBrowserState

  const handleGroupIdInUrl = useCallback(
    (newGroupId) => {
      setQuery({
        groupId: newGroupId,
      })
    },
    [setQuery]
  )

  /**
   * Handle updating the image ark in the url. This needs to change everytime an image is selected or navigated to.
   * This is important for feedback and for sharing urls.
   */
  const handleImageArkInUrl = useCallback(
    (newArk) => {
      if (isPageLoadRef.current) {
        isPageLoadRef.current = false
        return
      }
      const search = window.location.search
      const searchWithOutPersonArk = search
        ?.split('&')
        .filter((x) => !x.includes('personArk'))
        .filter((x) => !x.includes('action'))
        .join('&')

      history.push({
        pathname: `/ark:/61903/${newArk}`,
        search: searchWithOutPersonArk,
      })
      overlayContext.closeOverlay('viewIndex')
    },
    [history, overlayContext]
  )

  const setFocusedIndex = useCallback(
    (index) => {
      const setFocusedIndexCallback = () => {
        // See getImagesFromApidList.js for the thumbnail structure
        let thumbnail = null
        thumbnail = thumbnails[index]
        if (!thumbnail) return
        const { ark, id: apid } = thumbnail

        const groupId = getGroupIdByApidFromChildGroups(childGroups, apid)

        // Switch the groupId query if we select an image in a new item or add it to the url if it is not there
        if (viewMode === 'group' || query?.groupId !== groupId) {
          handleGroupIdInUrl(groupId)
        }

        handleImageArkInUrl(ark)
        // Set the focused index in the shared browser state
        sharedBrowserDispatch({ type: 'SET_FOCUSED_INDEX', value: index })
        zionSlsDispatch({ type: 'SET_DATA', value: { currentImageArk: ark } })
      }

      // All panels have to save first before we can move to the new image
      if (!doSwitchImageCheck || switchImageCheck(setFocusedIndexCallback)) {
        setFocusedIndexCallback()
      }
    },
    [
      switchImageCheck,
      doSwitchImageCheck,
      viewMode,
      thumbnails,
      sharedBrowserDispatch,
      handleGroupIdInUrl,
      handleImageArkInUrl,
      childGroups,
      zionSlsDispatch,
      query,
    ]
  )

  return setFocusedIndex
}

function getGroupIdByApidFromChildGroups(childGroups, apid) {
  const thumbnailGroup = childGroups.find((g) => g.childIds.includes(apid))
  return thumbnailGroup ? thumbnailGroup.parentId : null
}
